import {
  format,
  formatDistanceToNow,
  parseISO,
  isToday,
  isYesterday,
  isThisWeek,
  differenceInDays,
  startOfDay,
  differenceInCalendarDays,
} from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import type { Post } from '../types/post';

const DEFAULT_LANG = 'en';

export const getDateFnsLocale = (language?: string) => {
  const lang = (language || (typeof document !== 'undefined' ? document.documentElement.getAttribute('lang') : DEFAULT_LANG) || DEFAULT_LANG).toLowerCase();
  if (lang.startsWith('de')) {
    return de;
  }
  return enUS;
};

export const formatDate = (date: string | Date, formatStr: string = 'PPpp'): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, formatStr);
};

/** Encounter date/time on post tags: dot-separated date, 24h clock, local timezone (dd.MM.yyyy HH:mm). */
export const formatTagDateTime = (iso: string): string => {
  try {
    const dateObj = parseISO(iso);
    if (isNaN(dateObj.getTime())) return iso;
    return format(dateObj, 'dd.MM.yyyy HH:mm');
  } catch {
    return iso;
  }
};

export const formatRelativeTime = (date: string | Date, language?: string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatDistanceToNow(dateObj, { addSuffix: true, locale: getDateFnsLocale(language) });
};

/**
 * Format timestamp for messages - shows time for today, date for older messages
 */
export const formatMessageTime = (date: string | Date, language?: string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  if (isToday(dateObj)) {
    // Show time for today's messages (e.g., "2:30 PM")
    return format(dateObj, 'h:mm a');
  } else if (isYesterday(dateObj)) {
    // Show "Yesterday" for yesterday's messages
    return 'Yesterday';
  } else if (isThisWeek(dateObj)) {
    // Show day name for this week (e.g., "Monday")
    return format(dateObj, 'EEEE');
  } else {
    const daysDiff = differenceInDays(new Date(), dateObj);
    if (daysDiff < 7) {
      // Show "X days ago" for messages within a week
      return formatDistanceToNow(dateObj, { addSuffix: true, locale: getDateFnsLocale(language) });
    } else {
      // Show date for older messages (e.g., "Jan 15")
      return format(dateObj, 'MMM d');
    }
  }
};

/**
 * Format full timestamp with date and time
 */
export const formatFullTimestamp = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'MMM d, yyyy h:mm a');
};

export const isDateValid = (date: string | Date): boolean => {
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return !isNaN(dateObj.getTime());
  } catch {
    return false;
  }
};

/** Short clock time for timeline post meta (e.g. 2:30 PM). */
export const formatPostTimeShort = (date: string | Date, language?: string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'p', { locale: getDateFnsLocale(language) });
};

function hoursAgoFromNow(dateObj: Date): number {
  return (Date.now() - dateObj.getTime()) / (1000 * 60 * 60);
}

/**
 * Stable bucket key for grouping posts (newest-first) into timeline sections.
 * Recent posts use hour buckets; older posts use calendar-day buckets.
 */
export const getTimelineBucketKey = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const h = hoursAgoFromNow(dateObj);
  if (h < 0) {
    return `future-${dateObj.toISOString()}`;
  }
  if (h < 24) {
    const bucket = h < 1 ? 1 : Math.floor(h);
    return `h-${bucket}`;
  }
  const days = differenceInCalendarDays(startOfDay(new Date()), startOfDay(dateObj));
  return `d-${days}`;
};

/**
 * Section header for the horizontal bar: "9 Hours Ago", "One Day Ago", "3 Days Ago", or a calendar date.
 * Matches `getTimelineBucketKey` semantics.
 */
export const formatTimelineGroupLabel = (date: string | Date, language?: string): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const lang = (language || DEFAULT_LANG).toLowerCase();
  const isDe = lang.startsWith('de');
  const h = hoursAgoFromNow(dateObj);

  if (h < 0) {
    return format(dateObj, 'PP', { locale: getDateFnsLocale(language) });
  }

  if (h < 24) {
    const n = h < 1 ? 1 : Math.max(1, Math.floor(h));
    if (isDe) {
      return n === 1 ? 'vor 1 Stunde' : `vor ${n} Stunden`;
    }
    return n === 1 ? 'One Hour Ago' : `${n} Hours Ago`;
  }

  const days = differenceInCalendarDays(startOfDay(new Date()), startOfDay(dateObj));
  if (days === 1) {
    return isDe ? 'vor einem Tag' : 'One Day Ago';
  }
  if (days >= 2 && days <= 14) {
    return isDe ? `vor ${days} Tagen` : `${days} Days Ago`;
  }
  return format(dateObj, 'PP', { locale: getDateFnsLocale(language) });
};

/** Group posts (newest first) by timeline bucket for section headers and rails. */
export const groupPostsByTimelineBucket = (posts: Post[]): { bucketKey: string; posts: Post[] }[] => {
  const groups: { bucketKey: string; posts: Post[] }[] = [];
  for (const post of posts) {
    const bucketKey = getTimelineBucketKey(post.createdAt);
    const last = groups[groups.length - 1];
    if (last && last.bucketKey === bucketKey) {
      last.posts.push(post);
    } else {
      groups.push({ bucketKey, posts: [post] });
    }
  }
  return groups;
};

